import React, { useState, useCallback, useEffect } from "react";
import { Checkbox } from "@emisgroup/ui-checkbox";
import "./style.scss";
import { Skeleton } from "@emisgroup/ui-skeleton";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { SortDownIcon } from "./SortDownIcon";
import { SortUpIcon } from "./SortUpIcon";
import { DataEmpty } from "./DataEmpty/DataEmpty";
import { DecorativeEmpty } from "./DecorativeEmpty/DecorativeEmpty";
import { DataError } from "./DataError/DataError";
import { Refresh } from "./Refresh/Refresh";

export type PracticeRowValue = string | boolean | number | React.ReactNode;

export interface PracticeColumn<T extends PracticeRowData = PracticeRowData> {
  key: keyof T & string;
  header: string;
  width?: string;
  sortable?: boolean;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
}

export interface PracticeRowData {
  id: string;
  [key: string]: PracticeRowValue;
}

interface IPracticeTableProps<T extends PracticeRowData = PracticeRowData> {
  columns: PracticeColumn<T>[];
  rowData: T[];
  isSelection?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  selectedRows?: string[];
  onRowClicked?: (row: T) => void;
  isLoading?: boolean;
  isError?: boolean;
  refreshData?: () => void;
}

type SortDirection = "asc" | "desc" | null;

const TableSkeleton = (): JSX.Element => {
  return (
    <tbody data-testid='tableSkeleton'>
      { Array.from({ length: 20 }).map((_, i) => (
        <tr key={ i }>
          {
            Array.from({ length: 5 }).map((_, index) => (
              <td key={ index }>
                <Skeleton height="12px" variant="image" lines={2} />
              </td>
            ))
          }
        </tr>
      )) }
    </tbody>
  );
};


export const PracticeTable = <T extends PracticeRowData = PracticeRowData>({
  columns,
  rowData,
  isSelection = false,
  onSelectionChange,
  selectedRows = [],
  onRowClicked,
  isLoading,
  isError,
  refreshData
}: IPracticeTableProps<T>): React.ReactElement => {
  const { t: translate } = useTranslation();
  const noData = !rowData || rowData.length === 0;
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set(selectedRows));
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleRowClick = useCallback((row: T, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest(".checkbox-wrapper")) {
      return;
    }
    if (onRowClicked) {
      onRowClicked(row);
    }
  }, [onRowClicked]);

  const handleRowSelection = useCallback((rowId: string, isSelected: boolean) => {
    const newSelectedRows = new Set<string>(selectedRowIds);

    if (isSelected) {
      newSelectedRows.add(rowId);
    } else {
      newSelectedRows.delete(rowId);
    }

    setSelectedRowIds(newSelectedRows);

    if (onSelectionChange) {
      const selectedRowsData = rowData.filter(row => newSelectedRows.has(row.id));
      onSelectionChange(selectedRowsData);
    }
  }, [selectedRowIds, rowData, onSelectionChange]);

  const handleSelectAll = useCallback((isSelected: boolean) => {
    const newSelectedRows =
      isSelected ? new Set<string>(rowData.map(row => row.id)) : new Set<string>();
    setSelectedRowIds(newSelectedRows);

    if (onSelectionChange) {
      const selectedRowsData = isSelected ? [...rowData] : [];
      onSelectionChange(selectedRowsData);
    }
  }, [rowData, onSelectionChange]);

  useEffect(() => {
    setSelectedRowIds(new Set(selectedRows));
  }, [selectedRows]);

  if(isError) {
    return (
      <DataError
        title={ translate("SomethingWentWrong") }
        description={ translate("WeCouldNotPerformThisAction") }
        action={ {
          title: translate("TryAgain"),
          icon: <Refresh/>,
          callback: (): void => { refreshData?.(); }
        } }
      />
    );
  }

  if(!isLoading && noData) {
    return (
      <DataEmpty
        title={translate("NoDataAvailable")}
        description={translate("NoDataAvailableDescription")}
        icon={<DecorativeEmpty/>}
      />
    );
  }

  const isAllSelected = selectedRowIds.size === rowData.length && rowData.length > 0;
  const isIndeterminate = selectedRowIds.size > 0 && selectedRowIds.size < rowData.length;

  // Sorting logic
  const handleSort = (key: string): void => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : sortDirection === "desc" ? null : "asc");
      if (sortDirection === "desc") setSortKey(null);
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const getSortedData = (): T[] => {
    if (!sortKey || !sortDirection) return rowData;
    const sorted = [...rowData].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sortDirection === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return sorted;
  };

  const sortedData = getSortedData();

  const renderCell = (column: PracticeColumn<T>, row: T): React.ReactNode => {
    if (column.render) {
      return column.render(row[column.key], row);
    }
    return row[column.key] as React.ReactNode;
  };

  return (
    <table className="practice-table">
      <thead>
        <tr style={{ backgroundColor: "#f8f9fa" }}>
          {isSelection && (
            <th style={{ width: "50px" }}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
                aria-label="Select all rows"
              />
            </th>
          )}
          {columns.map((column) => (
            <th
              key={column.key}
              style={{
                width: column.width,
                cursor: column.sortable ? "pointer" : "default"
              }}
              onClick={column.sortable ? (): void => handleSort(column.key) : undefined}
            >
              <div className={`${column.sortable ? "sortable-header" : ""}`}>
                { column.sortable ? (<a>{column.header}</a>) : column.header}
                {column.sortable && (
                  <span style={{ height: "20px" }}>
                    {sortKey === column.key
                      ? sortDirection === "asc"
                        ? <SortUpIcon/>
                        : sortDirection === "desc"
                          ? <SortDownIcon/>
                          : ""
                      : ""}
                  </span>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <tbody>
          {sortedData.map((row) => {
            const isSelected = selectedRowIds.has(row.id);

            return (
              <tr
                key={row.id}
                onClick={(event): void => handleRowClick(row, event)}
              >
                {isSelection && (
                  <td className="practice-table-cell checkbox-wrapper" style={{ textAlign: "center" }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={(checked): void => handleRowSelection(row.id, checked)}
                      aria-label={`Select row ${row.id}`}
                    />
                  </td>
                )}
                {columns.map((column) => {
                  const width = column.width ? {
                    width: column.width,
                    minWidth: column.width,
                    maxWidth: column.width
                  } : {};

                  return (
                    <td
                      key={column.key}
                      className="practice-table-cell"
                      style={width}
                    >
                      {renderCell(column, row)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      )}
    </table>
  );
};
